// node_modules
import { FC, useContext, useRef, useState } from 'react';
// TypedChildren components
import { FindestButton, FindestRefTextBox } from 'Components';
// Stores
import { CaseContext } from 'Stores';
// Types
import { TTechnologyHighlightsGroupDTO } from 'Types';
// Controllers
import { TechnologyHighlightsControllerSingleton } from 'Controllers';
// Custom hooks
import { useClickOutsideRef } from 'Hooks';
// Styles
import styles from "./createTechnologyHighlightsGroup.module.scss";

type TCreateTechnologyHighlightsGroupProps = {
    technologyHighlightsGroups?: TTechnologyHighlightsGroupDTO[] | undefined,
    highlightId?: number | undefined,
    isInSidebar?: boolean,
    isInHighlightSection?: boolean,
    addTechnologyHighlightToTechnologyHighlightGroup?: ((technologyHighlightsGroup: TTechnologyHighlightsGroupDTO, highlightId: number) => void) | undefined,
    setNewSelectedGroupId: (newSelectedGroupId: number) => void
};

export const CreateTechnologyHighlightsGroup: FC<TCreateTechnologyHighlightsGroupProps> = ({ technologyHighlightsGroups, highlightId, isInSidebar, isInHighlightSection, addTechnologyHighlightToTechnologyHighlightGroup, setNewSelectedGroupId }: TCreateTechnologyHighlightsGroupProps) => {
    // Ref
    const groupNameInputRef = useRef<HTMLInputElement>(null);
    const technologyHighlightsGroupsRef = useRef<HTMLDivElement>(null);

    // State
    const [groupName, setGroupName] = useState<string | undefined>(undefined);
    const [isCreateGroupButtonShown, setIsCreateGroupButtonShown] = useState<boolean>(false);
    const [areTechnologyHighlightsGroupsShown, setAreTechnologyHighlightsGroupsShown] = useState<boolean>(false);

    // Contexts
    const { caseState } = useContext(CaseContext);

    // Logic
    const handleClickOutsideRef = (): void => {
        setAreTechnologyHighlightsGroupsShown(false);
    };
    
    useClickOutsideRef(technologyHighlightsGroupsRef, handleClickOutsideRef);

    const handleAddTechnologyHighlightToTechnologyHighlightGroup = (technologyHighlightsGroup: TTechnologyHighlightsGroupDTO): void => {
        if (highlightId && addTechnologyHighlightToTechnologyHighlightGroup && groupNameInputRef && groupNameInputRef.current) {
            addTechnologyHighlightToTechnologyHighlightGroup(technologyHighlightsGroup, highlightId);
            setAreTechnologyHighlightsGroupsShown(false);
            groupNameInputRef.current.value = "";
            setGroupName("");
            setIsCreateGroupButtonShown(false);
        }
    };

    const handleGroupNameOnChange = (newGroupName: string): void => {
        if (newGroupName) {
            setGroupName(newGroupName);
            setIsCreateGroupButtonShown(true);
            setAreTechnologyHighlightsGroupsShown(true);
        } else {
            setGroupName("");
            setIsCreateGroupButtonShown(false);
            setAreTechnologyHighlightsGroupsShown(false);
        }
    };

    const handleCreateOnClick = async (): Promise<void> => {
        // safety-check
        if (!caseState.selectedCaseId || 
            !groupNameInputRef.current || 
            groupName === undefined) return;

        // call API to create technology highlights group
        const newTechnologyHighlightsGroup: TTechnologyHighlightsGroupDTO | null = await TechnologyHighlightsControllerSingleton
            .createTechnologyHighlightsGroupAsync(caseState.selectedCaseId, groupName);
        
        if (newTechnologyHighlightsGroup) {
            if (addTechnologyHighlightToTechnologyHighlightGroup && highlightId) {
                addTechnologyHighlightToTechnologyHighlightGroup(newTechnologyHighlightsGroup, highlightId);
            }

            setAreTechnologyHighlightsGroupsShown(false);
            groupNameInputRef.current.value = "";
            setGroupName("");
            setIsCreateGroupButtonShown(false);
            setNewSelectedGroupId(newTechnologyHighlightsGroup.id);
        }
    };

    return (
        <div className={`${isInSidebar ? styles.createTechnologyHighlightsGroupInSidebarContainer : styles.createTechnologyHighlightsGroupInHighlightsContainer}`}>
            <FindestRefTextBox onEnter={handleCreateOnClick} isHighlightGroupInputElement={isInHighlightSection} isBorderless={isInSidebar || isInHighlightSection} isTransparent={isInSidebar} isLargeFont={isInSidebar} ref={groupNameInputRef} placeholder={"+ Create new group"} onChange={handleGroupNameOnChange} />
            {areTechnologyHighlightsGroupsShown &&
                technologyHighlightsGroups &&
                technologyHighlightsGroups.length >= 1 ?
                <div className={styles.technologyHighlightsGroupOptionsContainer} ref={technologyHighlightsGroupsRef}>
                    {technologyHighlightsGroups.map((group) => {
                        return (
                            <div
                                className={styles.technologyHighlightsGroupOption}
                                key={group.id}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => handleAddTechnologyHighlightToTechnologyHighlightGroup(group)}>
                                {group.name}
                            </div>
                        )
                    })}
                </div>
                :
                null
            }
            {isCreateGroupButtonShown ?
                <FindestButton extraClassName={styles.addGroupbutton} title={"Create"} onClick={handleCreateOnClick} />
                :
                null
            }
        </div>
    );
};
